<template>
  <v-combobox
    ref="element"
    v-model="computedModel"
    :hide-selected="hideSelectedInMenu"
    :multiple="multiple"
    :small-chips="multiplechips"
    :deletable-chips="multiplechips"
    :items="comboboxItems"
    dense
    :error-messages="errorMessages"
    :search-input.sync="searchInput"
    :placeholder="comboboxPlaceholder"
    :hide-no-data="hideNoData"
    outlined
    :clearable="effectiveClearable"
    :allow-overflow="allowOverflow"
    :prepend-inner-icon="prependiconinner"
    :hide-details="effectiveHideDetails"
    :disabled="disabled"
    :append-icon="appendiconinner"
    :item-text="itemtext"
    :item-value="itemvalue"
    :menu-props="comboboxMenuProps"
    :filter="menuItemFilter"
    class="pa-0 ma-0 cursor-pointer tag-combobox"
    :class="{ 'tag-combobox--menu-search': menuSearch }"
    :label="label"
    :id="id || undefined"
    :name="name || undefined"
    :autocomplete="autocomplete || undefined"
    :aria-label="ariaLabel || undefined"
    :aria-labelledby="ariaLabelledby || undefined"
    data-testid="tag-combobox"
    @keydown="onComboboxKeydown"
  >
    <template v-if="menuSearch" v-slot:prepend-item>
      <div
        class="tag-combobox__menu-search"
        data-testid="tag-combobox__menu-search"
        @click.stop
        @mousedown.stop
      >
        <v-text-field
          ref="menuSearchInput"
          v-model="menuSearchQuery"
          dense
          outlined
          hide-details
          autocomplete="off"
          :placeholder="searchPlaceholder"
          data-testid="tag-combobox__menu-search-input"
          @click.stop
          @mousedown.stop
          @keydown.native="onMenuSearchKeydown"
        >
          <template v-slot:append>
            <img
              src="/img/icons/fbc-icons/SearchIcon.svg"
              alt=""
              aria-hidden="true"
              class="tag-combobox__menu-search-icon"
              data-testid="tag-combobox__menu-search-icon"
            />
          </template>
        </v-text-field>
      </div>

      <div
        v-if="showMenuCheckboxes"
        class="tag-combobox__menu-list"
        data-testid="tag-combobox__menu-list"
      >
        <button
          v-if="showSelectAll && !trimmedMenuQuery"
          type="button"
          class="tag-combobox__menu-row tag-combobox__menu-select-all"
          data-testid="tag-combobox__menu-select-all"
          @click.stop.prevent="toggleSelectAllMenuItems"
        >
          <v-checkbox
            color="primary"
            :ripple="false"
            readonly
            hide-details
            dense
            class="tag-combobox__menu-checkbox"
            :input-value="isAllMenuItemsSelected"
            :indeterminate="isPartiallyMenuSelected"
            data-testid="tag-combobox__menu-select-all-checkbox"
          />
          <span class="tag-combobox__menu-item-title">{{ selectAllMenuLabel }}</span>
        </button>

        <p
          v-if="!filteredMenuItems.length && !canAddCustomTag"
          class="tag-combobox__menu-empty"
          data-testid="tag-combobox__menu-empty"
        >
          {{ emptyMessage }}
        </p>

        <button
          v-for="item in filteredMenuItems"
          :key="itemKey(item)"
          type="button"
          role="option"
          class="tag-combobox__menu-row"
          :class="{ 'tag-combobox__menu-row--selected': isMenuItemSelected(item) }"
          :aria-selected="isMenuItemSelected(item) ? 'true' : 'false'"
          :data-testid="`tag-combobox__menu-option-${itemLabel(item)}`"
          @click.stop.prevent="toggleMenuItem(item)"
        >
          <v-checkbox
            color="primary"
            :ripple="false"
            readonly
            hide-details
            dense
            class="tag-combobox__menu-checkbox"
            :input-value="isMenuItemSelected(item)"
          />
          <span class="tag-combobox__menu-item-title">{{ itemLabel(item) }}</span>
        </button>

        <button
          v-if="canAddCustomTag"
          type="button"
          class="tag-combobox__menu-add"
          data-testid="tag-combobox__menu-add"
          @click.stop.prevent="commitTypedTag()"
        >
          <span class="tag-combobox__menu-add-label">Add "{{ trimmedMenuQuery }}"</span>
        </button>
      </div>

      <v-divider v-else class="tag-combobox__menu-divider" />
    </template>

    <template v-if="showMenuCheckboxes" slot="no-data">
      <div class="d-none" data-testid="tag-combobox__menu-no-data" />
    </template>

    <template v-if="menuSearch && !showMenuCheckboxes" v-slot:item="{ item, on, attrs }">
      <v-list-item
        v-bind="attrs"
        class="tag-combobox__menu-item"
        data-testid="tag-combobox__menu-item"
        v-on="on"
      >
        <v-list-item-content class="py-0">
          <v-list-item-title class="tag-combobox__menu-item-title">
            {{ itemLabel(item) }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>

    <template v-slot:selection="{ attrs, item, parent, selected }">
      <v-chip
        v-bind="attrs"
        :input-value="selected"
        data-testid="tag-combobox__selected-chip"
        :color="multiple === false ? 'white' : tagcolor"
        class="tag-combobox__selected-chip pa-0 px-1 ma-0 noTransform"
      >
        <span
          class="tag-combobox__selected-item-text pa-0 ma-0 noTransform"
          data-testid="tag-combobox__selected-item"
        >
          {{ itemLabel(item) }}
        </span>
        <img
          v-if="multiple && chipClose && chipCloseImage"
          :src="chipCloseImage"
          alt=""
          class="tag-combobox__selected-item-close-img"
          aria-hidden="true"
          @click.stop="parent.selectItem(item)"
          data-testid="tag-combobox__selected-item-close-icon"
        />
        <v-icon
          v-else-if="multiple && chipClose"
          class="pa-0 tag-combobox__selected-item-close-icon"
          data-testid="tag-combobox__selected-item-close-icon"
          @click="parent.selectItem(item)"
        >mdi-close</v-icon>
      </v-chip>
    </template>
  </v-combobox>
</template>

<script>
import {
  DIALOG_MENU_Z_INDEX,
  positionMenuNearActivator,
  resolveMenuElement,
} from '@/utils/sync-attached-menu';
import {
  DUPLICATE_TAG_TIMEOUT_MS,
  dedupeTags,
  findTag,
  sameTags,
  tagKey,
  tagLabel,
} from './TagInputsUtils';

const MENU_MAX_HEIGHT = 320;

export default {
  data() {
    return {
      selected: [],
      select: '',
      searchInput: '',
      duplicateError: '',
      duplicateTimer: null,
      scrollCloseBinding: null,
      menuSearchQuery: '',
      menuCloseUnwatch: null,
      menuPinTimer: null,
      menuPlacement: '',
      ignoreScrollClose: false,
      scrollIgnoreTimer: null,
      duplicateEnterBinding: null,
      menuMouseGuard: null,
      committing: false,
    };
  },
  props: {
    tagcolor: {
      type: String,
      default: 'primary',
    },
    hidedetails: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default() {
        return [];
      },
    },
    isRequried: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    multiplechips: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [String, Array, Object],
      default: '',
    },
    prependiconinner: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    appendiconinner: {
      type: String,
      default: '',
    },
    error: {
      type: [String, Array],
      default() {
        return [];
      },
    },
    warnDuplicate: {
      type: Boolean,
      default: false,
    },
    allowCustom: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    itemtext: {
      type: String,
      default: 'name',
    },
    itemvalue: {
      type: String,
      default: 'value',
    },
    chipClose: {
      type: Boolean,
      default: true,
    },
    chipCloseImage: {
      type: String,
      default: '',
    },
    allowOverflow: {
      type: Boolean,
      default: false,
    },
    menuContentClass: {
      type: String,
      default: '',
    },
    menuSearch: {
      type: Boolean,
      default: false,
    },
    /** Checkbox rows. Defaults on for searchable multi-select (requires menuSearch + multiple). */
    menuCheckboxes: {
      type: Boolean,
      default: true,
    },
    /** Select-all row in checkbox menu (requires menuCheckboxes). */
    showSelectAll: {
      type: Boolean,
      default: false,
    },
    searchPlaceholder: {
      type: String,
      default: 'Search',
    },
    emptyMessage: {
      type: String,
      default: 'No tags found',
    },
    /** When false, the menu stays above the field. */
    menuOffsetY: {
      type: Boolean,
      default: true,
    },
    id: {
      type: String,
      default: '',
    },
    ariaLabel: {
      type: String,
      default: '',
    },
    ariaLabelledby: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    autocomplete: {
      type: String,
      default: '',
    },
  },
  computed: {
    errorMessages() {
      const parentErrors = Array.isArray(this.error)
        ? this.error.filter(Boolean)
        : (this.error ? [this.error] : []);
      if (this.hidedetails) return this.duplicateError ? [this.duplicateError] : [];
      if (this.duplicateError) return [...parentErrors, this.duplicateError];
      return parentErrors;
    },
    effectiveHideDetails() {
      if (this.duplicateError) return false;
      return this.hidedetails;
    },
    comboboxPlaceholder() {
      if (this.menuSearch && this.multiple) {
        const selected = this.selected;
        const hasSelection = Array.isArray(selected) ? selected.length > 0 : !!selected;
        return hasSelection ? '' : this.placeholder;
      }
      return this.placeholder;
    },
    comboboxMenuProps() {
      const contentClass = ['tag-combobox__menu', this.menuContentClass].filter(Boolean).join(' ');
      const base = {
        offsetY: this.menuOffsetY,
        maxHeight: MENU_MAX_HEIGHT,
        closeOnContentClick: false,
        contentClass,
      };
      if (!this.menuSearch) return base;

      const openAbove = this.menuOffsetY === false;
      return {
        ...base,
        offsetY: true,
        top: openAbove,
        bottom: !openAbove,
        offsetOverflow: false,
        attach: false,
        fixed: true,
        zIndex: DIALOG_MENU_Z_INDEX,
      };
    },
    showMenuCheckboxes() {
      return this.menuSearch && this.multiple && this.menuCheckboxes;
    },
    hideSelectedInMenu() {
      if (this.showMenuCheckboxes) return false;
      if (this.menuSearch && this.multiple) return false;
      return true;
    },
    hideNoData() {
      if (this.showMenuCheckboxes) return false;
      return !this.allowCustom;
    },
    comboboxItems() {
      // Checkbox mode renders its own list. An empty combobox list keeps
      // Vuetify from drawing a second copy and from dropping custom tags.
      return this.showMenuCheckboxes ? [] : this.sourceItems;
    },
    effectiveClearable() {
      if (this.menuSearch && this.multiple) return false;
      return true;
    },
    trimmedMenuQuery() {
      return String(this.menuSearchQuery || '').trim();
    },
    sourceItems() {
      return dedupeTags(this.items, this.itemtext, this.itemvalue).tags;
    },
    menuSourceItems() {
      return dedupeTags(
        [...this.sourceItems, ...(this.selected || [])],
        this.itemtext,
        this.itemvalue,
      ).tags;
    },
    filteredMenuItems() {
      const query = this.trimmedMenuQuery.toLowerCase();
      if (!query) return this.menuSourceItems;
      return this.menuSourceItems.filter((item) => this.itemLabel(item).toLowerCase().includes(query));
    },
    canAddCustomTag() {
      if (!this.allowCustom || !this.trimmedMenuQuery) return false;
      return !findTag(this.menuSourceItems, this.trimmedMenuQuery, this.itemtext, this.itemvalue);
    },
    isAllMenuItemsSelected() {
      const items = this.filteredMenuItems;
      if (!items.length) return false;
      return items.every((item) => this.isMenuItemSelected(item));
    },
    isPartiallyMenuSelected() {
      const items = this.filteredMenuItems;
      if (!items.length) return false;
      const selectedCount = items.filter((item) => this.isMenuItemSelected(item)).length;
      return selectedCount > 0 && selectedCount < items.length;
    },
    selectAllMenuLabel() {
      return this.isAllMenuItemsSelected ? 'Unselect all' : 'Select all';
    },
    computedModel: {
      get() {
        return this.multiple ? this.selected : this.select;
      },
      set(val) {
        if (this.committing) return;
        if (this.multiple) {
          const incoming = Array.isArray(val) ? val : (val ? [val] : []);
          this.commitMultiple(incoming, { fromUser: true });
          return;
        }
        if (!this.isAllowedSelection(val)) {
          this.clearActivatorSearch();
          return;
        }
        this.select = val && typeof val === 'object'
          ? (val[this.itemvalue] ?? val[this.itemtext] ?? '')
          : (val || '');
        this.emitModel();
      },
    },
  },
  watch: {
    searchInput(newVal, oldVal) {
      if (this.duplicateError && newVal && newVal !== oldVal) this.clearDuplicateError();
    },
    menuSearchQuery() {
      this.repositionOpenMenu();
    },
    value: {
      immediate: true,
      handler(newVal) {
        if (this.committing) return;
        if (this.multiple) {
          const { tags } = dedupeTags(Array.isArray(newVal) ? newVal : [], this.itemtext, this.itemvalue);
          if (sameTags(this.selected, tags, this.itemtext, this.itemvalue)) return;
          this.selected = tags;
          this.forceComboboxValue(tags);
          return;
        }
        this.select = newVal || '';
      },
    },
  },
    mounted() {
      this.$nextTick(() => {
        this.bindScrollClose();
        this.bindMenuCloseReset();
        this.bindDuplicateEnterGuard();
        this.applyNativeInputAttrs();
      });
    },
  updated() {
    this.$nextTick(() => {
      this.bindScrollClose();
      this.bindMenuCloseReset();
      this.bindDuplicateEnterGuard();
      this.applyNativeInputAttrs();
    });
  },
  beforeDestroy() {
    this.unbindScrollClose();
    this.unbindMenuMouseGuard();
    this.menuCloseUnwatch?.();
    this.menuCloseUnwatch = null;
    this.clearMenuPinTimer();
    if (this.scrollIgnoreTimer) {
      window.clearTimeout(this.scrollIgnoreTimer);
      this.scrollIgnoreTimer = null;
    }
    this.unbindDuplicateEnterGuard();
    this.clearDuplicateError();
    this.selected = [];
    this.select = '';
    this.menuSearchQuery = '';
  },
  methods: {
    itemLabel(item) {
      return tagLabel(item, this.itemtext, this.itemvalue);
    },
    itemKey(item) {
      return tagKey(item, this.itemtext, this.itemvalue);
    },
    menuItemFilter(item, queryText, itemText) {
      if (this.showMenuCheckboxes) return false;
      if (!this.menuSearch) {
        const query = String(queryText || '').trim().toLowerCase();
        if (!query) return true;
        return String(itemText || this.itemLabel(item)).toLowerCase().includes(query);
      }
      const query = this.trimmedMenuQuery.toLowerCase();
      if (!query) return true;
      return this.itemLabel(item).toLowerCase().includes(query);
    },
    hasSelectedKey(key) {
      if (!key) return false;
      return (this.selected || []).some((item) => this.itemKey(item) === key);
    },
    isExistingItem(item) {
      const key = this.itemKey(item);
      if (!key) return false;
      return this.sourceItems.some((entry) => this.itemKey(entry) === key);
    },
    isAllowedSelection(item) {
      if (this.allowCustom) return true;
      if (item == null || item === '') return true;
      if (typeof item === 'string' && !item.trim()) return true;
      return this.isExistingItem(item);
    },
    isMenuItemSelected(item) {
      return this.hasSelectedKey(this.itemKey(item));
    },
    showDuplicateError(value) {
      const label = String(value || '').trim();
      if (!label) return;
      this.duplicateError = `${label} already exists.`;
      if (this.duplicateTimer) window.clearTimeout(this.duplicateTimer);
      this.duplicateTimer = window.setTimeout(() => {
        this.duplicateError = '';
        this.duplicateTimer = null;
      }, DUPLICATE_TAG_TIMEOUT_MS);
    },
    clearDuplicateError() {
      if (this.duplicateTimer) {
        window.clearTimeout(this.duplicateTimer);
        this.duplicateTimer = null;
      }
      this.duplicateError = '';
    },
    clearActivatorSearch() {
      this.searchInput = null;
      const field = this.$refs.element;
      if (field) field.internalSearch = null;
    },
    emitModel() {
      const value = this.multiple ? this.selected : this.select;
      this.$emit('input', value);
      this.$emit('change', value);
    },
    forceComboboxValue(value) {
      const field = this.$refs.element;
      if (!field) return;
      field.lazyValue = this.multiple ? [...(value || [])] : value;
    },
    commitMultiple(incoming, { fromUser = false } = {}) {
      if (this.committing) return;

      let list = Array.isArray(incoming) ? incoming : [];
      if (!this.allowCustom) {
        list = list.filter((item) => this.isExistingItem(item) || this.hasSelectedKey(this.itemKey(item)));
      }

      const { tags, duplicateLabel } = dedupeTags(list, this.itemtext, this.itemvalue);
      const changed = !sameTags(this.selected, tags, this.itemtext, this.itemvalue);
      const showDuplicate = fromUser && duplicateLabel && (this.warnDuplicate || this.menuSearch);

      if (showDuplicate) {
        this.showDuplicateError(duplicateLabel);
        this.clearActivatorSearch();
      } else if (changed) {
        this.clearDuplicateError();
      }

      if (!changed && !showDuplicate) return;

      this.committing = true;
      if (changed) {
        this.selected = tags;
        this.emitModel();
      }
      this.forceComboboxValue(this.selected);
      this.committing = false;

      if (fromUser && this.menuSearch) this.stabilizeOpenMenuAfterSelection();
    },
    toggleMenuItem(item) {
      const key = this.itemKey(item);
      if (!key) return;
      const next = this.isMenuItemSelected(item)
        ? (this.selected || []).filter((entry) => this.itemKey(entry) !== key)
        : [...(this.selected || []), item];
      this.commitMultiple(next, { fromUser: true });
    },
    toggleSelectAllMenuItems() {
      const visible = this.filteredMenuItems;
      if (this.isAllMenuItemsSelected) {
        const removeKeys = new Set(visible.map((item) => this.itemKey(item)));
        const next = (this.selected || []).filter((entry) => !removeKeys.has(this.itemKey(entry)));
        this.commitMultiple(next, { fromUser: true });
        return;
      }
      this.commitMultiple([...(this.selected || []), ...visible], { fromUser: true });
    },
    commitTypedTag(raw) {
      const fromArg = (typeof raw === 'string' || typeof raw === 'number') ? raw : '';
      const typed = String(fromArg || this.trimmedMenuQuery || this.searchInput || '').trim();
      if (!typed) return;

      const existing = findTag(this.menuSourceItems, typed, this.itemtext, this.itemvalue);
      if (existing) {
        if (this.isMenuItemSelected(existing)) {
          this.showDuplicateError(this.itemLabel(existing));
          return;
        }
        this.toggleMenuItem(existing);
        this.menuSearchQuery = '';
        this.clearActivatorSearch();
        return;
      }

      if (!this.allowCustom) return;
      this.commitMultiple([...(this.selected || []), typed], { fromUser: true });
      this.menuSearchQuery = '';
      this.clearActivatorSearch();
    },
    onMenuSearchKeydown(event) {
      event.stopPropagation();
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.commitTypedTag();
    },
    onComboboxKeydown(event) {
      if (event.key !== 'Enter') return;
      if (this.showMenuCheckboxes) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
        const typed = String(this.searchInput || this.$refs.element?.internalSearch || '').trim();
        if (typed) this.commitTypedTag(typed);
        return;
      }
      const typed = String(this.searchInput || '').trim();
      if (!this.allowCustom && typed && !this.isExistingItem(typed)) {
        event.preventDefault();
        event.stopPropagation();
        this.clearActivatorSearch();
        return;
      }
      if (!this.multiple || !typed || !this.hasSelectedKey(typed.toLowerCase())) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      if (this.warnDuplicate || this.menuSearch) this.showDuplicateError(typed);
      this.clearActivatorSearch();
    },
    bindDuplicateEnterGuard() {
      if (this.duplicateEnterBinding || !this.multiple) return;
      const input = this.$refs.element?.$refs?.input;
      if (!input) return;
      const handler = (event) => this.onComboboxKeydown(event);
      input.addEventListener('keydown', handler, true);
      this.duplicateEnterBinding = { input, handler };
    },
    unbindDuplicateEnterGuard() {
      if (!this.duplicateEnterBinding) return;
      const { input, handler } = this.duplicateEnterBinding;
      input.removeEventListener('keydown', handler, true);
      this.duplicateEnterBinding = null;
    },
    applyNativeInputAttrs() {
      const input = this.$refs.element?.$refs?.input
        || this.$refs.element?.$el?.querySelector?.('input');
      if (!input) return;
      if (this.autocomplete) input.setAttribute('autocomplete', this.autocomplete);
      if (this.name) input.setAttribute('name', this.name);
      if (this.id) input.setAttribute('id', this.id);
    },
    bindMenuCloseReset() {
      if (this.menuCloseUnwatch || !this.menuSearch) return;
      this.menuCloseUnwatch = this.$watch(
        () => this.$refs.element?.isMenuActive,
        (active) => {
          if (active) {
            this.menuSearchQuery = '';
            this.menuPlacement = this.menuOffsetY === false ? 'above' : 'below';
            this.suppressScrollClose();
            this.$nextTick(() => {
              requestAnimationFrame(() => {
                this.bindMenuMouseGuard();
                this.refreshOpenMenuPosition({ initial: true });
                this.focusMenuSearchInput();
              });
            });
            return;
          }
          this.menuSearchQuery = '';
          this.menuPlacement = '';
          this.unbindMenuMouseGuard();
        },
      );
    },
    bindMenuMouseGuard() {
      if (this.menuMouseGuard) return;
      const menu = resolveMenuElement({
        menuClass: this.menuContentClass,
        fieldRef: this.$refs.element,
      });
      if (!menu) return;
      const handler = (event) => {
        if (event.target?.closest?.('.tag-combobox__menu-search')) return;
        event.preventDefault();
      };
      menu.addEventListener('mousedown', handler);
      this.menuMouseGuard = { menu, handler };
    },
    unbindMenuMouseGuard() {
      if (!this.menuMouseGuard) return;
      const { menu, handler } = this.menuMouseGuard;
      menu.removeEventListener('mousedown', handler);
      this.menuMouseGuard = null;
    },
    pinMenuToField() {
      if (!this.menuSearch || !this.$refs.element?.isMenuActive) return;
      if (!this.menuPlacement) {
        this.menuPlacement = this.menuOffsetY === false ? 'above' : 'below';
      }
      positionMenuNearActivator({
        activatorEl: this.$refs.element?.$el,
        menuClass: this.menuContentClass,
        fieldRef: this.$refs.element,
        maxMenuHeight: MENU_MAX_HEIGHT,
        prefer: this.menuPlacement,
      });
    },
    clearMenuPinTimer() {
      if (!this.menuPinTimer) return;
      window.clearTimeout(this.menuPinTimer);
      this.menuPinTimer = null;
    },
    refreshOpenMenuPosition({ initial = false } = {}) {
      this.pinMenuToField();
      if (!initial) return;
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.pinMenuToField();
          this.clearMenuPinTimer();
          this.menuPinTimer = window.setTimeout(() => {
            this.menuPinTimer = null;
            this.pinMenuToField();
          }, 80);
        });
      });
    },
    stabilizeOpenMenuAfterSelection() {
      if (!this.menuSearch || !this.multiple || !this.$refs.element?.isMenuActive) return;
      this.suppressScrollClose(500);
      this.refreshOpenMenuPosition({ initial: true });
    },
    repositionOpenMenu() {
      if (!this.menuSearch || !this.$refs.element?.isMenuActive) return;
      this.$nextTick(() => {
        requestAnimationFrame(() => this.pinMenuToField());
      });
    },
    focusMenuSearchInput() {
      const field = this.$refs.element;
      if (field) field.internalSearch = null;
      const fieldRef = this.$refs.menuSearchInput;
      const input = fieldRef?.$refs?.input || fieldRef?.$el?.querySelector?.('input');
      if (input) {
        input.focus();
        return;
      }
      const scope = this.menuContentClass
        ? `.${this.menuContentClass.split(/\s+/)[0]}`
        : '.v-menu__content--active';
      document.querySelector(`${scope} [data-testid="tag-combobox__menu-search-input"] input`)?.focus();
    },
    findScrollParent(node) {
      let el = node?.parentElement;
      while (el) {
        const style = window.getComputedStyle(el);
        const overflow = `${style.overflow}${style.overflowY}${style.overflowX}`;
        if (/(auto|scroll|overlay)/.test(overflow)) return el;
        el = el.parentElement;
      }
      return null;
    },
    bindScrollClose() {
      if (this.scrollCloseBinding) return;
      const parent = this.findScrollParent(this.$el);
      if (!parent) return;
      const handler = () => {
        if (this.ignoreScrollClose) return;
        if (this.$refs.element?.isMenuActive) this.closeMenu();
      };
      parent.addEventListener('scroll', handler, { passive: true });
      this.scrollCloseBinding = { parent, handler };
    },
    suppressScrollClose(ms = 250) {
      this.ignoreScrollClose = true;
      if (this.scrollIgnoreTimer) window.clearTimeout(this.scrollIgnoreTimer);
      this.scrollIgnoreTimer = window.setTimeout(() => {
        this.ignoreScrollClose = false;
        this.scrollIgnoreTimer = null;
      }, ms);
    },
    unbindScrollClose() {
      if (!this.scrollCloseBinding) return;
      const { parent, handler } = this.scrollCloseBinding;
      parent.removeEventListener('scroll', handler);
      this.scrollCloseBinding = null;
    },
    closeMenu() {
      const field = this.$refs.element;
      if (!field) return;
      try {
        field.isMenuActive = false;
        if (field.$refs?.menu) field.$refs.menu.isActive = false;
        if (typeof field.blur === 'function') field.blur();
      } catch (_) {
        /* menu may already be gone */
      }
    },
  },
};
</script>

<style src="./TagInputs.scss" lang="scss"></style>
